var _CsvParser;
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Papa from 'papaparse';
import Log from '@deephaven/log';
import { assertNotNull, DbNameValidator } from '@deephaven/utils';
import CsvTypeParser from "./CsvTypeParser.js";
import makeZipStreamHelper from "./ZipStreamHelper.js";
var log = Log.module('CsvParser');

// This is based on jszip streaming ~15 KB chunks to papa parse
// Want to consolidate to ~10 MB chunks
var ZIP_CONSOLIDATE_CHUNKS = 650;
/**
 * Parser a CSV file in chunks and returns a table handle for each chunk.
 */
class CsvParser {
  static generateHeaderRecursive(n) {
    var header = '';
    var char = n;
    if (n >= 26) {
      header = header.concat(CsvParser.generateHeaderRecursive(Math.floor(n / 26) - 1));
      char = n % 26;
    }
    return header.concat(String.fromCharCode(65 + char));
  }
  constructor(_ref) {
    var onFileCompleted = _ref.onFileCompleted,
      session = _ref.session,
      file = _ref.file,
      type = _ref.type,
      _ref$readHeaders = _ref.readHeaders,
      readHeaders = _ref$readHeaders === void 0 ? true : _ref$readHeaders,
      onProgress = _ref.onProgress,
      onError = _ref.onError,
      timeZone = _ref.timeZone,
      isZip = _ref.isZip;
    _defineProperty(this, "onFileCompleted", void 0);
    _defineProperty(this, "session", void 0);
    _defineProperty(this, "file", void 0);
    _defineProperty(this, "isZip", void 0);
    _defineProperty(this, "type", void 0);
    _defineProperty(this, "readHeaders", void 0);
    _defineProperty(this, "timeZone", void 0);
    _defineProperty(this, "onProgress", void 0);
    _defineProperty(this, "onError", void 0);
    _defineProperty(this, "tables", void 0);
    _defineProperty(this, "headers", void 0);
    _defineProperty(this, "types", void 0);
    _defineProperty(this, "rowCount", void 0);
    _defineProperty(this, "rowsProcessed", void 0);
    _defineProperty(this, "chunks", void 0);
    _defineProperty(this, "totalChunks", void 0);
    _defineProperty(this, "isComplete", void 0);
    _defineProperty(this, "zipProgress", void 0);
    _defineProperty(this, "consolidatedChunks", void 0);
    _defineProperty(this, "numConsolidated", void 0);
    _defineProperty(this, "isCancelled", void 0);
    _defineProperty(this, "config", void 0);
    this.onFileCompleted = onFileCompleted;
    this.session = session;
    this.file = file;
    this.isZip = isZip;
    this.type = type;
    this.readHeaders = readHeaders;
    this.timeZone = timeZone;
    this.onProgress = onProgress;
    this.onError = onError;
    this.tables = [];
    this.rowCount = 0;
    this.rowsProcessed = 0;
    this.chunks = 0;
    this.totalChunks = isZip ? 0 : Math.ceil(file.size / Papa.LocalChunkSize);
    this.isComplete = false;
    this.zipProgress = 0;
    this.numConsolidated = 0;
    this.isCancelled = false;
    this.handleChunk = this.handleChunk.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleComplete = this.handleComplete.bind(this);
    this.handleNodeUpdate = this.handleNodeUpdate.bind(this);
    this.handleCreateTable = this.handleCreateTable.bind(this);
    this.config = {
      delimiter: type.delimiter,
      newline: type.newline,
      escapeChar: type.escapeChar,
      dynamicTyping: false,
      error: this.handleError,
      skipEmptyLines: type.skipEmptyLines,
      chunk: this.handleChunk,
      complete: this.handleComplete,
      fastMode: false
    };
  }
  cancel() {
    this.isCancelled = true;
  }
  transpose(numColumns, array) {
    var numRows = array.length;
    var columns = new Array(numColumns).fill(null).map(() => new Array(numRows));
    for (var r = 0; r < numRows; r += 1) {
      var row = array[r];
      if (row.length < numColumns) {
        throw new Error("Insufficient columns. Expected ".concat(numColumns, " but found ").concat(row.length, "\n").concat(row));
      }
      for (var c = 0; c < numColumns; c += 1) {
        var value = this.type.shouldTrim ? array[r][c].trim() : array[r][c];
        columns[c][r] = this.nullCheck(value);
      }
    }
    return columns;
  }
  nullCheck(value) {
    return value === this.type.nullString ? '' : value;
  }
  parse() {
    var handleParseDone = (types, rowCount) => {
      this.types = types;
      this.rowCount = rowCount;
      if (this.file instanceof File || this.file instanceof Blob) {
        Papa.parse(this.file, this.config);
      } else {
        var zipStream = makeZipStreamHelper(this.file, this.handleNodeUpdate);
        // This is actually a stream, but papaparse TS doesn't like it
        Papa.parse(zipStream, this.config);
        // The stream needs to be manually resumed since jszip starts paused
        // Papaparse does not call resume and assumes the stream is already reading
        zipStream.resume();
      }
    };
    var typeParser = new CsvTypeParser(handleParseDone, this.file, this.readHeaders, this.config, this.type.nullString, this.onProgress, this.onError, this.totalChunks, this.isZip, this.type.shouldTrim);
    typeParser.parse();
  }
  handleChunk(result, parser) {
    var readHeaders = this.readHeaders,
      onError = this.onError,
      handleCreateTable = this.handleCreateTable,
      isZip = this.isZip,
      tables = this.tables;
    if (this.isCancelled) {
      log.debug2('CSV parser cancelled.');
      parser.abort();
      tables.forEach(t => t.close());
      return;
    }
    var data = result.data;
    if (!this.headers) {
      if (readHeaders) {
        this.headers = DbNameValidator.legalizeColumnNames(data[0]);
        data = data.slice(1);
      } else {
        this.headers = CsvParser.generateHeaders(data[0].length);
      }
    }
    var columns = [];
    try {
      columns = this.transpose(this.headers.length, data);
      if (isZip) {
        // Zip file chunks are tiny, so consolidate them to avoid create thousands of small tables
        this.consolidateChunks(columns, parser);
      } else {
        var index = this.chunks;
        this.chunks += 1;
        handleCreateTable(index, columns, parser);
      }
    } catch (e) {
      onError(e);
    }
  }
  consolidateChunks(columns, parser) {
    if (!this.consolidatedChunks) {
      this.consolidatedChunks = columns.slice();
    } else {
      for (var i = 0; i < columns.length; i += 1) {
        this.consolidatedChunks[i] = this.consolidatedChunks[i].concat(columns[i]);
      }
    }
    this.numConsolidated += 1;
    if (this.numConsolidated >= ZIP_CONSOLIDATE_CHUNKS || this.isComplete) {
      this.uploadConsolidatedChunks(parser);
    }
  }
  uploadConsolidatedChunks(parser) {
    var _this$consolidatedChu;
    var handleCreateTable = this.handleCreateTable;
    var index = this.chunks;
    this.chunks += 1;
    var toUpload = (_this$consolidatedChu = this.consolidatedChunks) === null || _this$consolidatedChu === void 0 ? void 0 : _this$consolidatedChu.slice();
    this.consolidatedChunks = undefined;
    this.numConsolidated = 0;
    assertNotNull(toUpload);
    handleCreateTable(index, toUpload, parser);
  }
  handleCreateTable(index, columns, parser) {
    var session = this.session,
      tables = this.tables,
      onFileCompleted = this.onFileCompleted,
      totalChunks = this.totalChunks,
      types = this.types,
      onProgress = this.onProgress,
      onError = this.onError;
    if (parser) {
      parser.pause();
    }
    assertNotNull(this.headers);
    assertNotNull(types);
    this.rowsProcessed += columns[0].length;
    session.newTable(this.headers, types, columns, this.timeZone).then(table => {
      if (this.isCancelled) {
        log.debug2('CSV parser cancelled.');
        if (parser) {
          parser.abort();
        }
        tables.forEach(t => t.close());
        return;
      }
      if (parser) {
        parser.resume();
      }
      tables[index] = table;
      // This accounts for 50% of parsing plus 50% already done by the type parser
      var progress = 0;
      if (totalChunks > 0) {
        progress = Math.round(tables.length / totalChunks * 50) + 50;
      } else {
        // The zip file can be read entirely while in the middle of parsing
        // Since we know the number of rows from the type parsing, use that for progress
        progress = Math.round(this.rowsProcessed / this.rowCount * 50) + 50;
      }
      log.debug2("CSV parser progress ".concat(progress));
      onProgress(progress);
      if (this.isComplete && tables.length === this.chunks) {
        log.debug2('CSV parser complete.');
        onFileCompleted(tables);
      }
    }).catch(e => {
      if (!this.isComplete && parser) {
        parser.abort();
      }
      onError(e);
    });
  }
  handleComplete(results) {
    // results is undefined for a succesful parse, but has meta data for an abort
    if (results == null || results.meta.aborted == null) {
      this.isComplete = true;
      // Check if there are any consolidated chunks left over from a zip file
      if (this.consolidatedChunks) {
        this.uploadConsolidatedChunks(null);
      }
    }
  }
  handleError(error) {
    var onError = this.onError;
    onError(error);
  }
  handleNodeUpdate(metadata) {
    this.zipProgress = metadata.percent;
  }
}
_CsvParser = CsvParser;
// Generates column names A-Z, AA-AZ, BA-BZ, etc...
_defineProperty(CsvParser, "generateHeaders", numColumns => {
  var headers = [];
  for (var i = 0; i < numColumns; i += 1) {
    headers.push(_CsvParser.generateHeaderRecursive(i));
  }
  return headers;
});
export default CsvParser;
//# sourceMappingURL=CsvParser.js.map